import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { endOfMonth, format, startOfMonth } from "date-fns";
import toast from "react-hot-toast";
import {
  Building2,
  CalendarClock,
  CheckCircle,
  CreditCard,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  FileCode,
  FileText,
  Loader2,
  Plus,
  Repeat,
  RotateCcw,
  SlidersHorizontal,
  XCircle,
} from "lucide-react";

function formatMetricValue(uyu: number, usd: number): string {
  const parts: string[] = [];
  if (uyu > 0 || (uyu === 0 && usd === 0)) {
    parts.push(`UYU ${Number(uyu).toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  }
  if (usd > 0) {
    parts.push(`USD ${Number(usd).toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "—";
}

import { isConsumerFinalReceiver } from "@fiscalo/shared-types";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  invoiceCreationBlockedTooltipKey,
  useInvoiceCreationBlocked,
} from "@/hooks/useInvoiceCreationBlocked";
import { useReissueRejectedInvoice } from "@/hooks/useReissueRejectedInvoice";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import { clientKeys, tenantKeys } from "@/lib/queryKeys";
import { useAuth } from "@/contexts/AuthContext";
import { getTenantCurrent } from "@/services/authService";
import { listTenantMembers } from "@/services/tenantUsersService";
import { invoiceKeys } from "@/lib/queryKeys";
import { cn } from "@/lib/utils";
import { InvoiceMobileCard } from "@/pages/invoices/InvoiceMobileCard";
import { InvoicesSummaryCard, InvoicesSummaryCardSkeleton } from "@/pages/invoices/InvoicesSummaryCard";
import {
  buildInvoiceListActions,
  formatComprobanteLabel,
  formatTotal,
  groupInvoicesWithChildren,
  invoiceHasAdjustments,
  isCreditNote,
  isDebitNote,
  resolveConsumerFinalName,
  resolveInvoiceDisplayStatus,
  statusIcon,
  statusVariant,
  type GroupedInvoiceRow,
  type ReissueRef,
} from "@/pages/invoices/invoice-list-shared";
import { listClients, type Client } from "@/services/clientService";
import { annulInvoice, deleteDraftInvoice, exportInvoicesCsv, exportInvoicesXmlZip, listInvoices, queueInvoiceSubmission } from "@/services/invoiceService";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import {
  InvoicesListFilters,
  VALID_PERIODS,
} from "@/pages/invoices/invoices-list-filters";
const ISO_DATE = "yyyy-MM-dd";

function getCurrentMonthRange(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  return {
    dateFrom: format(startOfMonth(now), ISO_DATE),
    dateTo: format(endOfMonth(now), ISO_DATE),
  };
}

type ReissueChipNavigate = (path: string) => void;
type ReissueChipT = import("i18next").TFunction;

function ReissueChip({
  reissueRef,
  t,
  navigate,
}: {
  reissueRef: ReissueRef;
  t: ReissueChipT;
  navigate: ReissueChipNavigate;
}): JSX.Element {
  const targetLabel = formatComprobanteLabel(
    reissueRef.targetCfeType,
    reissueRef.targetInternalNumber,
    t
  );
  const text =
    reissueRef.direction === "successorOf"
      ? t("invoices.list.reissueOf", "Reemite {{label}}", { label: targetLabel })
      : t("invoices.list.reissuedBy", "Reemitida por {{label}}", { label: targetLabel });
  return (
    <button
      type="button"
      data-interactive
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/invoices/${reissueRef.targetId}`);
      }}
      className="inline-flex shrink-0 items-center gap-1 rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 text-xs font-normal text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground"
    >
      <RotateCcw className="h-3 w-3" aria-hidden />
      <span>{text}</span>
    </button>
  );
}

export function InvoicesPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") ?? undefined;
  const rawPeriod = searchParams.get("period") ?? undefined;
  const period =
    !rawPeriod ||
    rawPeriod === "all" ||
    !VALID_PERIODS.includes(rawPeriod as (typeof VALID_PERIODS)[number])
      ? "month"
      : rawPeriod;
  const dateFrom = searchParams.get("dateFrom") ?? undefined;
  const dateTo = searchParams.get("dateTo") ?? undefined;
  const clientId = searchParams.get("client") ?? undefined;
  const currency = searchParams.get("currency") ?? undefined;
  const createdByUserId = searchParams.get("createdByUserId") ?? undefined;
  const pagination = usePaginationFromUrl();

  const queueSubmissionMutation = useMutation({
    mutationFn: (invoiceId: string) => queueInvoiceSubmission(invoiceId),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      if (data.action === "noop") {
        toast.success(
          t(
            "invoices.list.queueSubmissionNoop",
            "Nothing to queue; refresh the list if the status already updated."
          )
        );
      } else if (data.action === "enqueued") {
        toast.success(
          t(
            "invoices.list.queueSubmissionEnqueued",
            "Submission queued. It will be sent to DGI shortly."
          )
        );
      } else {
        toast.success(
          t(
            "invoices.list.queueSubmissionRescheduled",
            "Processing queued. It will run shortly."
          )
        );
      }
    },
    onError: () => {
      toast.error(
        t(
          "invoices.list.queueSubmissionError",
          "Could not queue submission. Try again."
        )
      );
    },
  });

  const queueSubmissionLoadingId =
    queueSubmissionMutation.isPending && queueSubmissionMutation.variables != null
      ? queueSubmissionMutation.variables
      : null;

  const annulMutation = useMutation({
    mutationFn: (invoiceId: string) => annulInvoice(invoiceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast.success(t("invoices.list.annulSuccess", "Comprobante anulado."));
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast.error(
          t(
            "invoices.list.annulNotAllowed",
            "Este comprobante fue aceptado por la DGI; para revertirlo emití una nota de crédito."
          )
        );
        return;
      }
      toast.error(t("invoices.list.annulError", "No se pudo anular el comprobante."));
    },
  });

  const annulLoadingId =
    annulMutation.isPending && annulMutation.variables != null
      ? annulMutation.variables
      : null;

  const onAnnulInvoice = useCallback(
    (invoiceId: string) => {
      if (
        !window.confirm(
          t(
            "invoices.list.annulConfirm",
            "¿Anular este comprobante? El número quedará anulado y se reportará como anulado a la DGI. Esta acción no se puede deshacer."
          )
        )
      ) {
        return;
      }
      annulMutation.mutate(invoiceId);
    },
    [annulMutation, t]
  );

  const [draftToDelete, setDraftToDelete] = useState<string | null>(null);

  const deleteDraftMutation = useMutation({
    mutationFn: (invoiceId: string) => deleteDraftInvoice(invoiceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast.success(t("invoices.list.deleteDraftSuccess", "Borrador eliminado."));
      setDraftToDelete(null);
    },
    onError: () => {
      toast.error(t("invoices.list.deleteDraftError", "No se pudo eliminar el borrador."));
    },
  });

  const deleteDraftLoadingId =
    deleteDraftMutation.isPending && deleteDraftMutation.variables != null
      ? deleteDraftMutation.variables
      : null;

  const { reissue: reissueRejected } = useReissueRejectedInvoice();

  const periodMonthLabels = useMemo(() => {
    const locale = i18n.language ?? "es";
    const now = new Date();
    const thisMonthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(now);
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(lastMonthDate);
    return { thisMonthName, lastMonthName };
  }, [i18n.language]);

  const { data: clientsData } = useQuery({
    queryKey: clientKeys.list({ page: 1, limit: 100 }),
    queryFn: () => listClients({ page: 1, limit: 100 }),
  });
  const clients: Client[] = clientsData?.data ?? [];

  const { data: tenantData } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const { data: membersData } = useQuery({
    queryKey: ["tenant-members", tenantData?.id],
    queryFn: () => listTenantMembers(tenantData!.id),
    enabled: !!tenantData?.id,
  });
  const users = useMemo(
    () =>
      (membersData ?? []).map((m) => ({
        userId: m.userId,
        displayName: [m.firstName, m.lastName].filter(Boolean).join(" ") || m.displayName,
        email: m.email,
      })),
    [membersData]
  );

  const setFilter = useCallback(
    (key: "status" | "period" | "client" | "currency" | "createdByUserId", value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          if (key === "period") {
            next.delete("dateFrom");
            next.delete("dateTo");
            if (value === "custom") {
              const { dateFrom: from, dateTo: to } = getCurrentMonthRange();
              next.set("dateFrom", from);
              next.set("dateTo", to);
            }
          }
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setCustomDateRange = useCallback(
    (from: string, to: string) => {
      const fromDate = new Date(from);
      const toDate = new Date(to);
      if (fromDate > toDate) {
        toast.error(
          t("recibidas.dateFromBeforeDateTo", "From date must be on or before to date.")
        );
        return;
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("dateFrom", from);
          next.set("dateTo", to);
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, t]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("status");
        next.delete("period");
        next.delete("dateFrom");
        next.delete("dateTo");
        next.delete("client");
        next.delete("currency");
        next.set("page", "1");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const { blocked: invoicingBlocked, reason: invoicingBlockedReason } =
    useInvoiceCreationBlocked();
  const disabledNewInvoiceTooltipKey =
    invoiceCreationBlockedTooltipKey(invoicingBlockedReason);

  const hasActiveFilters = Boolean(
    status || period !== "month" || clientId || currency
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (period !== "month") n++;
    if (status) n++;
    if (clientId) n++;
    if (currency) n++;
    return n;
  }, [period, status, clientId, currency]);

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingXml, setIsExportingXml] = useState(false);
  const [expandedRejectedAncestors, setExpandedRejectedAncestors] = useState<Set<string>>(
    () => new Set()
  );
  const toggleRejectedAncestor = useCallback((primaryId: string): void => {
    setExpandedRejectedAncestors((prev) => {
      const next = new Set(prev);
      if (next.has(primaryId)) next.delete(primaryId);
      else next.add(primaryId);
      return next;
    });
  }, []);

  const {
    data,
    isLoading,
    isFetching,
    isPlaceholderData,
    isError,
    refetch,
  } = useQuery({
    queryKey: invoiceKeys.list({
      page: pagination.page,
      limit: pagination.limit,
      status,
      period,
      dateFrom: period === "custom" ? dateFrom : undefined,
      dateTo: period === "custom" ? dateTo : undefined,
      customerId: clientId,
      currency,
      createdByUserId,
    }),
    queryFn: () =>
      listInvoices({
        page: pagination.page,
        limit: pagination.limit,
        status,
        period,
        dateFrom: period === "custom" ? dateFrom : undefined,
        dateTo: period === "custom" ? dateTo : undefined,
        customerId: clientId,
        currency,
        createdByUserId,
      }),
    placeholderData: keepPreviousData,
  });

  /**
   * Same filters as the CSV export, so the two always describe the same rows.
   *
   * The result is reported rather than assumed: historical CFEs we imported
   * were never submitted by us, so no XML exists for them and they cannot be
   * regenerated either. Handing back a quietly short ZIP would look identical
   * to a complete one.
   */
  const handleExportXml = useCallback(async () => {
    setIsExportingXml(true);
    try {
      const { exported, skipped, excluded } = await exportInvoicesXmlZip({
        status,
        period,
        dateFrom: period === "custom" ? dateFrom : undefined,
        dateTo: period === "custom" ? dateTo : undefined,
        customerId: clientId,
        currency,
        createdByUserId,
      });
      if (exported === 0) {
        toast.error(
          t(
            "invoices.list.exportXmlNone",
            "No hay XML disponibles para los comprobantes filtrados."
          )
        );
      } else if (skipped > 0 || excluded > 0) {
        // A narrowed export must never read as a complete one. `excluded` is
        // "not eligible" (DGI never accepted it, or it was not reported);
        // `skipped` is "eligible but we hold no filed XML".
        const parts = [
          t("invoices.list.exportXmlOk", {
            exported,
            defaultValue: "{{exported}} XML exportados.",
          }),
        ];
        if (excluded > 0) {
          parts.push(
            t("invoices.list.exportXmlExcluded", {
              excluded,
              defaultValue:
                "{{excluded}} excluidos (no aceptados por DGI o aún no reportados).",
            })
          );
        }
        if (skipped > 0) {
          parts.push(
            t("invoices.list.exportXmlSkipped", {
              skipped,
              defaultValue: "{{skipped}} sin XML disponible.",
            })
          );
        }
        toast.success(parts.join(" "));
      } else {
        toast.success(
          t("invoices.list.exportXmlOk", {
            exported,
            defaultValue: "{{exported}} XML exportados.",
          })
        );
      }
    } catch {
      toast.error(
        t("invoices.list.exportError", "No se pudo exportar. Intentá de nuevo.")
      );
    } finally {
      setIsExportingXml(false);
    }
  }, [status, period, dateFrom, dateTo, clientId, currency, createdByUserId, t]);

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      await exportInvoicesCsv({
        status,
        period,
        dateFrom: period === "custom" ? dateFrom : undefined,
        dateTo: period === "custom" ? dateTo : undefined,
        customerId: clientId,
        currency,
        createdByUserId,
      });
    } catch {
      toast.error(
        t("invoices.list.exportError", "No se pudo exportar. Intentá de nuevo.")
      );
    } finally {
      setIsExporting(false);
    }
  }, [status, period, dateFrom, dateTo, clientId, currency, createdByUserId, t]);

  useEffect(() => {
    if (period === "custom" && (!dateFrom || !dateTo)) {
      const { dateFrom: from, dateTo: to } = getCurrentMonthRange();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("dateFrom", from);
          next.set("dateTo", to);
          return next;
        },
        { replace: true }
      );
    }
  }, [period, dateFrom, dateTo, setSearchParams]);

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("invoices.list.error", "Unable to load invoices. The API may not be available yet."));
    }
  }, [isError, t]);

  const invoices = data?.data ?? [];
  const summary = data?.summary;

  const summaryPeriodLabel = useMemo(() => {
    if (period === "month") {
      const name = periodMonthLabels.thisMonthName;
      return t("invoices.list.summaryCard.periodMonth", {
        month: name.charAt(0).toUpperCase() + name.slice(1),
        defaultValue: "{{month}} — mes en curso",
      });
    }
    if (period === "last_month") {
      const name = periodMonthLabels.lastMonthName;
      return t("invoices.list.summaryCard.periodLastMonth", {
        month: name.charAt(0).toUpperCase() + name.slice(1),
        defaultValue: "{{month}} — mes anterior",
      });
    }
    if (period === "24h") {
      return t("invoices.list.summaryCard.period24h", "Últimas 24 horas");
    }
    if (period === "7d") {
      return t("invoices.list.summaryCard.period7d", "Últimos 7 días");
    }
    if (period === "custom" && dateFrom && dateTo) {
      return t("invoices.list.summaryCard.periodCustom", {
        from: dateFrom,
        to: dateTo,
        defaultValue: "{{from}} → {{to}}",
      });
    }
    return t("invoices.list.summaryCard.periodAll", "Todos los períodos");
  }, [period, periodMonthLabels, dateFrom, dateTo, t]);

  return (
    <div className="fx-content">
      <PageHeader
        eyebrow={t("invoices.list.eyebrow", "Comprobantes emitidos")}
        title={t("invoices.list.title", "Invoices")}
        actions={
          <>
            {invoicingBlocked && disabledNewInvoiceTooltipKey ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <button type="button" className="fx-btn fx-btn-accent" disabled aria-disabled="true">
                      <Plus className="h-4 w-4" aria-hidden />
                      {t("invoices.list.newInvoice", "New invoice")}
                    </button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t(disabledNewInvoiceTooltipKey)}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Link to="/invoices/new" className="fx-btn fx-btn-accent">
                <Plus className="h-4 w-4" aria-hidden />
                {t("invoices.list.newInvoice", "New invoice")}
              </Link>
            )}
            {invoicingBlocked && disabledNewInvoiceTooltipKey ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <button type="button" className="fx-btn fx-btn-ghost" disabled aria-disabled="true">
                      <CalendarClock className="h-4 w-4" aria-hidden />
                      {t("invoices.list.addRecurringInvoice", "Add recurring invoice")}
                    </button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t(disabledNewInvoiceTooltipKey)}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Link to="/invoices/recurring/new" className="fx-btn fx-btn-ghost">
                <CalendarClock className="h-4 w-4" aria-hidden />
                {t("invoices.list.addRecurringInvoice", "Add recurring invoice")}
              </Link>
            )}
          </>
        }
      />

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {/* Período — always visible */}
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-sm text-muted-foreground">
            {t("invoices.list.filterPeriod", "Date range")}
          </span>
          {/* Picking "Personalizado" opens the Filters dialog, because that is
              where this page's date pickers live — InvoicesListFilters is only
              rendered inside the dialog. Without this, choosing Personalizado
              silently seeds the current month and shows no way to change it.
              The sibling lists do not need this: recibidas and reports reveal
              their pickers inline under the select instead. */}
          <Select
            value={period}
            onValueChange={(v) => {
              setFilter("period", v);
              if (v === "custom") setMobileFiltersOpen(true);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder={t("invoices.list.filterPeriod", "Período")}>
                {period === "month"
                  ? periodMonthLabels.thisMonthName.charAt(0).toUpperCase() + periodMonthLabels.thisMonthName.slice(1)
                  : period === "last_month"
                    ? periodMonthLabels.lastMonthName.charAt(0).toUpperCase() + periodMonthLabels.lastMonthName.slice(1)
                    : period === "24h"
                      ? t("invoices.list.period24h", "Últimas 24h")
                      : period === "7d"
                        ? t("invoices.list.period7d", "Últimos 7 días")
                        : period === "custom"
                          ? t("invoices.list.periodCustom", "Personalizado")
                          : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">{t("invoices.list.period24h", "Last 24 hours")}</SelectItem>
              <SelectItem value="7d">{t("invoices.list.period7d", "Last 7 days")}</SelectItem>
              <SelectItem value="month">
                {t("invoices.list.periodMonthWithName", { month: periodMonthLabels.thisMonthName })}
              </SelectItem>
              <SelectItem value="last_month">
                {t("invoices.list.periodLastMonthWithName", { month: periodMonthLabels.lastMonthName })}
              </SelectItem>
              <SelectItem value="custom">{t("invoices.list.periodCustom", "Custom")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Other filters — in dialog */}
        <Dialog open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 gap-2 transition-colors duration-150"
            >
              <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden />
              <span className="truncate">{t("invoices.list.filters", "Filters")}</span>
              {activeFilterCount > 0 ? (
                <Badge
                  variant="secondary"
                  className="shrink-0 tabular-nums"
                  aria-label={t("invoices.list.filtersActiveShort", {
                    count: activeFilterCount,
                  })}
                >
                  {activeFilterCount}
                </Badge>
              ) : null}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[min(90vh,720px)] w-[calc(100vw-1.5rem)] max-w-lg gap-0 overflow-hidden p-0 sm:rounded-xl">
            <DialogHeader className="border-b border-border px-4 py-4 text-left">
              <DialogTitle className="text-foreground">
                {t("invoices.list.filtersSheetTitle", "Filter invoices")}
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[min(70vh,560px)] overflow-y-auto overscroll-contain px-4 py-4">
              <InvoicesListFilters
                t={t}
                periodMonthLabels={periodMonthLabels}
                period={period}
                status={status}
                clientId={clientId}
                currency={currency}
                createdByUserId={createdByUserId}
                dateFrom={dateFrom}
                dateTo={dateTo}
                clients={clients}
                users={users}
                setFilter={setFilter}
                setCustomDateRange={setCustomDateRange}
                getCurrentMonthRange={getCurrentMonthRange}
                layout="stacked"
                className="flex flex-col gap-5"
              />
            </div>
            <div
              className={cn(
                "flex flex-col gap-2 border-t border-border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center",
                hasActiveFilters ? "sm:justify-between" : "sm:justify-end"
              )}
            >
              {hasActiveFilters ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full transition-colors duration-150 sm:w-auto"
                  onClick={() => {
                    clearFilters();
                  }}
                >
                  {t("invoices.list.clearFilters", "Clear filters")}
                </Button>
              ) : null}
              <Button
                type="button"
                className="w-full transition-colors duration-150 sm:w-auto"
                onClick={() => setMobileFiltersOpen(false)}
              >
                {t("invoices.list.filtersDone", "Done")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {hasActiveFilters ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full basis-full shrink-0 text-muted-foreground transition-colors duration-150 sm:w-auto sm:basis-auto"
            onClick={clearFilters}
          >
            {t("invoices.list.clearFilters", "Clear filters")}
          </Button>
        ) : null}

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-10 gap-2 transition-colors duration-150 sm:ml-auto"
          onClick={handleExportCsv}
          disabled={isExporting || (data?.total ?? 0) === 0}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <Download className="h-4 w-4 shrink-0" aria-hidden />
          )}
          <span className="truncate">{t("invoices.list.exportCsv", "Exportar CSV")}</span>
        </Button>

        {/* Same filters and the same disabled rule as the CSV button — two
            adjacent exports must describe the same rows. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-10 gap-2 transition-colors duration-150"
          onClick={handleExportXml}
          disabled={isExportingXml || (data?.total ?? 0) === 0}
        >
          {isExportingXml ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <FileCode className="h-4 w-4 shrink-0" aria-hidden />
          )}
          <span className="truncate">{t("invoices.list.exportXml", "Exportar XML")}</span>
        </Button>
      </div>

      {summary && !isPlaceholderData ? (
        <InvoicesSummaryCard summary={summary} periodLabel={summaryPeriodLabel} />
      ) : isLoading || isPlaceholderData ? (
        <InvoicesSummaryCardSkeleton />
      ) : null}

      <TableCard className="mt-4">
        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : invoices.length === 0 ? (
          <EmptyState
            icon={<FileText />}
            title={t("invoices.list.empty", "No invoices yet.")}
            message={t("invoices.list.emptyHint", "Create your first invoice to get started.")}
            action={
              invoicingBlocked && disabledNewInvoiceTooltipKey ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button disabled aria-disabled="true">
                        <Plus className="mr-2 h-4 w-4" aria-hidden />
                        {t("invoices.list.newInvoice", "New invoice")}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t(disabledNewInvoiceTooltipKey)}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button asChild>
                  <Link to="/invoices/new" className="inline-flex items-center">
                    <Plus className="mr-2 h-4 w-4" aria-hidden />
                    {t("invoices.list.newInvoice", "New invoice")}
                  </Link>
                </Button>
              )
            }
          />
        ) : (
          (() => {
            const groupedRows = groupInvoicesWithChildren(invoices);
            // Track which primary id each rejectedAncestor row sits under so
            // we can hide them when collapsed.
            let currentPrimaryId: string | null = null;
            const visibleRows = groupedRows.filter((row) => {
              if (!row.isChild) {
                currentPrimaryId = row.inv.id;
                return true;
              }
              if (row.childKind === "rejectedAncestor") {
                return currentPrimaryId
                  ? expandedRejectedAncestors.has(currentPrimaryId)
                  : false;
              }
              return true;
            });
            return (
          <div className="relative">
            {isFetching && (
              <div
                className="absolute inset-0 z-10 flex min-h-[200px] items-center justify-center rounded-lg bg-background/80 backdrop-blur-[1px]"
                aria-busy="true"
                aria-label={t("invoices.list.loading", "Loading…")}
              >
                <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
              </div>
            )}
            <div className="space-y-3 py-2 md:hidden">
              {visibleRows.map((row) => (
                <InvoiceMobileCard
                  key={row.inv.id}
                  invoice={row.inv}
                  isChild={row.isChild}
                  childKind={row.childKind}
                  reissueRef={row.reissueRef}
                  hasRejectedAncestor={row.hasRejectedAncestor}
                  rejectedAncestorCount={row.rejectedAncestorCount}
                  isAncestorExpanded={expandedRejectedAncestors.has(row.inv.id)}
                  onToggleRejectedAncestor={() => toggleRejectedAncestor(row.inv.id)}
                  invoicingBlocked={invoicingBlocked}
                  onQueueSubmission={(invoiceId) =>
                    queueSubmissionMutation.mutate(invoiceId)
                  }
                  queueSubmissionLoadingId={queueSubmissionLoadingId}
                  onReissueRejected={(invoiceId, cfeType, referencedInvoiceId) => {
                    reissueRejected({ invoiceId, cfeType, referencedInvoiceId });
                  }}
                  reissueLoadingId={null}
                  onDeleteDraft={(invoiceId) => setDraftToDelete(invoiceId)}
                  deleteDraftLoadingId={deleteDraftLoadingId}
                />
              ))}
            </div>
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("invoices.list.date", "Date")}</TableHead>
                  <TableHead>{t("invoices.list.customer", "Customer")}</TableHead>
                  <TableHead>{t("invoices.list.comprobante", "Document")}</TableHead>
                  <TableHead>{t("invoices.list.paymentMethod", "Payment method")}</TableHead>
                  <TableHead>{t("invoices.list.createdBy", "Created by")}</TableHead>
                  <TableHead>{t("invoices.list.status", "Status")}</TableHead>
                  <TableHead className="text-right">{t("invoices.list.total", "Total")}</TableHead>
                  <TableHead className="w-[52px] text-right" aria-hidden />
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.map((row) => {
                  const { inv, isChild, childKind, reissueRef, hasRejectedAncestor, rejectedAncestorCount } = row;
                  const isRejectedAncestor = childKind === "rejectedAncestor";
                  const isAncestorExpanded = expandedRejectedAncestors.has(inv.id);
                  const ancestorCount = rejectedAncestorCount ?? 0;
                  const displayStatus = resolveInvoiceDisplayStatus(inv);
                  const StatusIcon = statusIcon(displayStatus);
                  const listLabel =
                    t(`invoices.list.statusLabel.${displayStatus}`, displayStatus) ||
                    t(`invoices.status.${displayStatus}`, displayStatus);
                  const isRejected = displayStatus === "rejected";
                  const receiverRut = inv.receiverSnapshot?.rut;
                  const receiverRutOrDoc = inv.receiverSnapshot?.docRecepExt?.trim()
                    ? inv.receiverSnapshot.docRecepExt
                    : receiverRut?.trim() && receiverRut !== "000000000000"
                      ? receiverRut
                      : undefined;
                  const isConsumerFinal = isConsumerFinalReceiver(receiverRut) && !inv.customerId;
                  const consumerFinalName = isConsumerFinal
                    ? resolveConsumerFinalName(inv.receiverSnapshot?.name)
                    : null;
                  const clientDisplayName = (inv as unknown as Record<string, unknown>).clientDisplayName as string | undefined;

                  const hasAdjustments = invoiceHasAdjustments(inv);
                  const rowActions = buildInvoiceListActions(inv, {
                    t,
                    navigate,
                    invoicingBlocked,
                    onQueueSubmission: (invoiceId) =>
                      queueSubmissionMutation.mutate(invoiceId),
                    queueSubmissionLoadingId,
                    onReissueRejected: (invoiceId, cfeType, referencedInvoiceId) => {
                      reissueRejected({ invoiceId, cfeType, referencedInvoiceId });
                    },
                    reissueLoadingId: null,
                    onAnnul: onAnnulInvoice,
                    annulLoadingId,
                    onDeleteDraft: (invoiceId) => setDraftToDelete(invoiceId),
                    deleteDraftLoadingId,
                  });

                  return (
                    <TableRow
                      key={inv.id}
                      onRowClick={() => navigate(`/invoices/${inv.id}`)}
                      className={cn(
                        isChild && "bg-muted/30",
                        isRejectedAncestor && "opacity-60"
                      )}
                    >
                      <TableCell className="text-muted-foreground">
                        {inv.issueDate
                          ? formatDisplayDate(inv.issueDate, {
                              locale: i18n.language,
                              useCalendarDate: true,
                            })
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <div className={cn("flex flex-col", isChild && childKind === "creditNote" && "opacity-70")}>
                          {isConsumerFinal ? (
                            <>
                              <span className="font-medium">
                                {consumerFinalName ??
                                  t("invoices.list.consumerFinal", "Consumer final")}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {consumerFinalName
                                  ? t(
                                      "invoices.list.consumerFinalNamedHelp",
                                      "Consumer final - no fiscal identification required"
                                    )
                                  : t(
                                      "invoices.list.consumerFinalHelp",
                                      "No fiscal identification required"
                                    )}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="font-medium">
                                {clientDisplayName || inv.receiverSnapshot?.name || "—"}
                              </span>
                              {receiverRutOrDoc &&
                                receiverRutOrDoc !== "000000000000" && (
                                <span className="text-xs text-muted-foreground tabular-nums">
                                  {receiverRutOrDoc}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className={cn("flex flex-wrap items-center gap-1.5", isChild && "pl-4")}>
                          {isChild && (
                            <span className="text-muted-foreground" aria-hidden>{"↳"}</span>
                          )}
                          {isCreditNote(inv.cfeType) && (
                            <Badge variant="secondary" className="text-xs font-medium shrink-0">
                              {t("invoices.list.creditNoteShort", "NC")}
                            </Badge>
                          )}
                          {isDebitNote(inv.cfeType) && (
                            <Badge variant="secondary" className="text-xs font-medium shrink-0">
                              {t("invoices.list.debitNoteShort", "ND")}
                            </Badge>
                          )}
                          <span>{formatComprobanteLabel(inv.cfeType, inv.internalNumber, t)}</span>
                          {/* Marca chip. Only present on invoices that carry
                              one, so a tenant without marcas sees the column
                              exactly as before. The name comes from the server
                              with the row, so it cannot disagree with the
                              identity the client was billed under. */}
                          {(inv as unknown as Record<string, unknown>).brandName ? (
                            <Badge variant="outline" className="text-xs font-medium shrink-0">
                              {String((inv as unknown as Record<string, unknown>).brandName)}
                            </Badge>
                          ) : null}
                          {hasAdjustments && (
                            <span className="text-xs text-muted-foreground shrink-0">
                              {t("invoices.list.adjusted", "Adjusted")}
                            </span>
                          )}
                          {/* Hide the "Reemite e-Ticket X" successor chip
                             once the retry was accepted — the green
                             "Enviada" badge + the inline "Ver intento
                             rechazado" toggle already make the chain
                             obvious; the chip becomes visual noise. Keep
                             it for pending/draft retries (still useful as
                             "this is a retry in flight") and for the
                             predecessor chip on the rejected row (helps
                             confirm the rejection is already addressed). */}
                          {reissueRef &&
                            !(
                              reissueRef.direction === "successorOf" &&
                              displayStatus === "sent"
                            ) && (
                            <ReissueChip
                              reissueRef={reissueRef}
                              t={t}
                              navigate={navigate}
                            />
                          )}
                          {hasRejectedAncestor && (
                            <button
                              type="button"
                              data-interactive
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRejectedAncestor(inv.id);
                              }}
                              className="ml-1 inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                            >
                              {isAncestorExpanded ? (
                                <ChevronDown className="h-3 w-3" aria-hidden />
                              ) : (
                                <ChevronRight className="h-3 w-3" aria-hidden />
                              )}
                              {isAncestorExpanded
                                ? ancestorCount === 1
                                  ? t("invoices.list.hideRejectedAttempt", "Ocultar intento")
                                  : t("invoices.list.hideRejectedAttempts", "Ocultar intentos")
                                : ancestorCount === 1
                                  ? t("invoices.list.viewRejectedAttempt", "Ver intento rechazado")
                                  : t("invoices.list.viewRejectedAttemptsCount", "Ver {{count}} intentos rechazados", { count: ancestorCount })}
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground capitalize">
                        {inv.paymentMethod === "credito"
                          ? t("invoices.list.paymentMethodCredito", "Credit")
                          : t("invoices.list.paymentMethodContado", "Spot")}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const r = inv as unknown as Record<string, unknown>;
                          const createdVia = r.createdVia as string | undefined;
                          if (createdVia === "subscription") {
                            return (
                              <StatusBadge variant="info" className="inline-flex w-fit">
                                <CreditCard className="h-3 w-3 shrink-0" aria-hidden />
                                <span>{t("invoices.list.createdBySubscription", "Fiscalo subscription")}</span>
                              </StatusBadge>
                            );
                          }
                          const apiKeyName = r.createdByApiKeyName as string | undefined;
                          if (apiKeyName) {
                            return (
                              <div className="flex flex-col">
                                <span className="font-medium">API</span>
                                <span className="text-xs text-muted-foreground">{apiKeyName}</span>
                              </div>
                            );
                          }
                          const name = r.createdByUserName as string | undefined;
                          const email = r.createdByUserEmail as string | undefined;
                          if (createdVia === "deel") {
                            return (
                              <StatusBadge variant="info" className="inline-flex w-fit">
                                <Building2 className="h-3 w-3 shrink-0" aria-hidden />
                                <span>{t("invoices.list.createdByDeel", "Deel")}</span>
                              </StatusBadge>
                            );
                          }
                          if (createdVia === "recurring") {
                            return (
                              <div className="flex flex-col gap-1">
                                <StatusBadge variant="info" className="inline-flex w-fit">
                                  <Repeat className="h-3 w-3 shrink-0" aria-hidden />
                                  <span>{t("invoices.list.createdByRecurring", "Factura recurrente")}</span>
                                </StatusBadge>
                                {name && (
                                  <span className="text-xs text-muted-foreground">
                                    {t("invoices.list.createdByRecurringSetupBy", "Configurada por {{name}}", { name })}
                                  </span>
                                )}
                              </div>
                            );
                          }
                          if (inv.status === "draft" && inv.scheduledSendAt) {
                            return (
                              <div className="flex flex-col gap-1">
                                <StatusBadge variant="info" className="inline-flex w-fit">
                                  <CalendarClock className="h-3 w-3 shrink-0" aria-hidden />
                                  <span>{t("invoices.schedule.scheduled", "Comprobante programado")}</span>
                                </StatusBadge>
                                <span className="text-xs text-muted-foreground">
                                  {t("invoices.schedule.scheduledFor", "Programada para {{dateTime}}", {
                                    dateTime: formatDisplayDate(inv.scheduledSendAt, {
                                      includeTime: true,
                                      locale: i18n.language,
                                    }),
                                  })}
                                </span>
                                {/* This is the "Creado por" column: the badge
                                    describes the schedule, but the operator
                                    must not disappear from it. The recurring
                                    branch above keeps the name for the same
                                    reason. */}
                                {(name || email) && (
                                  <span className="text-xs text-muted-foreground">
                                    {name || email}
                                  </span>
                                )}
                              </div>
                            );
                          }
                          if (!name && !email) return null;
                          return (
                            <div className="flex flex-col">
                              {name && <span className="font-medium">{name}</span>}
                              {email && (
                                <span className="text-xs text-muted-foreground">{email}</span>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="w-[200px] min-w-[200px] max-w-[200px]">
                        <div className="flex min-w-0 max-w-full">
                          {(() => {
                            const raw = inv as unknown as Record<string, unknown>;
                            const receptorAck = raw.receptorAckCfeEstado as string | undefined;
                            const acuseRecibido = !!raw.acuseReciboReceivedAt;
                            if (isRejected) {
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex min-w-0 max-w-full">
                                      <StatusBadge variant={statusVariant(displayStatus)} className="inline-flex max-w-full">
                                        <StatusIcon className="h-3 w-3 shrink-0" aria-hidden />
                                        <span className="min-w-0 truncate">{listLabel}</span>
                                      </StatusBadge>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[280px]">
                                    {t("invoices.list.rejectedBadgeTooltip", "This invoice was rejected by DGI and has no fiscal effect. A new invoice must be issued.")}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            }
                            // Receptor's ACKCFE — the strongest "all good" /
                            // "needs attention" signal. AE supersedes the
                            // ACKSobre badge (recibida is implied by aceptada);
                            // RE warns the user the receptor refused the CFE.
                            if (receptorAck === "AE") {
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex min-w-0 max-w-full">
                                      <StatusBadge variant="success" className="inline-flex max-w-full">
                                        <CheckCheck className="h-3 w-3 shrink-0" aria-hidden />
                                        <span className="min-w-0 truncate">{t("invoices.list.sentReceivedAndAccepted", "Enviada y aceptada")}</span>
                                      </StatusBadge>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[280px]">
                                    {t("invoices.list.sentReceivedAndAcceptedTooltip", "El receptor confirmó la recepción y aceptó la factura con un ACKCFE firmado (DGI §XVIII).")}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            }
                            if (receptorAck === "RE") {
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex min-w-0 max-w-full">
                                      <StatusBadge variant="warning" className="inline-flex max-w-full">
                                        <StatusIcon className="h-3 w-3 shrink-0" aria-hidden />
                                        <span className="min-w-0 truncate">{t("invoices.list.rejectedByReceptor", "Rechazada por receptor")}</span>
                                      </StatusBadge>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[280px]">
                                    {t("invoices.list.rejectedByReceptorTooltip", "El receptor rechazó la factura con un ACKCFE firmado. La factura mantiene efecto fiscal frente a DGI pero el cliente no la reconoce.")}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            }
                            if (acuseRecibido) {
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex min-w-0 max-w-full">
                                      <StatusBadge variant="success" className="inline-flex max-w-full">
                                        <CheckCheck className="h-3 w-3 shrink-0" aria-hidden />
                                        <span className="min-w-0 truncate">{t("invoices.list.sentAndReceived", "Enviada y recibida")}</span>
                                      </StatusBadge>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[280px]">
                                    {t("invoices.list.sentAndReceivedTooltip", "La factura fue enviada al receptor y se recibió el acuse de recibo confirmando la recepción. Aplica solo para empresas uruguayas con RUT registrado en DGI.")}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            }
                            return (
                              <StatusBadge variant={statusVariant(displayStatus)} className="inline-flex max-w-full">
                                <StatusIcon className="h-3 w-3 shrink-0" aria-hidden />
                                <span className="min-w-0 truncate">{listLabel}</span>
                              </StatusBadge>
                            );
                          })()}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatTotal(inv.totals?.total?.currency, inv.totals?.total?.amount)}
                      </TableCell>
                      <TableCell
                        className="w-[52px] text-right"
                        onClick={(e) => e.stopPropagation()}
                        data-interactive
                      >
                        <div className="flex justify-end">
                          <TableActionsMenu actions={rowActions} />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("invoices.list.rowsPerPage", "Rows per page")}
              pageOfLabel={t("invoices.list.pageOf", "{{from}}-{{to}} of {{total}}", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </div>
            );
          })()
        )}
      </TableCard>

      <ConfirmationDialog
        open={draftToDelete != null}
        onOpenChange={(open) => {
          if (!open) setDraftToDelete(null);
        }}
        title={t("invoices.list.deleteDraftConfirmTitle", "¿Eliminar borrador?")}
        description={t(
          "invoices.list.deleteDraftConfirmDescription",
          "Este borrador se eliminará permanentemente. Esta acción no se puede deshacer."
        )}
        confirmText={t("invoices.list.deleteDraft", "Eliminar borrador")}
        cancelText={t("pages.common.cancel")}
        variant="destructive"
        isLoading={deleteDraftMutation.isPending}
        onConfirm={() => {
          if (draftToDelete) deleteDraftMutation.mutate(draftToDelete);
        }}
      />
    </div>
  );
}
